const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type PersistedJobIdentity = {
  id?: unknown;
  org_id?: unknown;
  title?: unknown;
  status?: unknown;
  publish_idempotency_key?: unknown;
};

type JobCommandResult = {
  success?: unknown;
  published?: unknown;
  draft_id?: unknown;
  job?: unknown;
};

export class JobCommandResultError extends Error {
  readonly code: 'JOB_PERSISTENCE_UNCONFIRMED' | 'JOB_COMMAND_RESULT_MISMATCH';
  readonly resetCommand: boolean;

  constructor(code: JobCommandResultError['code'], resetCommand = false) {
    super(code);
    this.name = 'JobCommandResultError';
    this.code = code;
    this.resetCommand = resetCommand;
  }
}

function normalizedTitle(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function assertExpectedIdentity(args: {
  job: PersistedJobIdentity;
  orgId: string;
  title: string;
  publish: boolean;
  commandKey: string;
}) {
  const expectedStatus = args.publish ? 'active' : 'draft';
  if (
    typeof args.job.id !== 'string' ||
    !UUID_PATTERN.test(args.job.id) ||
    args.job.org_id !== args.orgId ||
    normalizedTitle(args.job.title) !== normalizedTitle(args.title) ||
    args.job.status !== expectedStatus ||
    args.job.publish_idempotency_key !== args.commandKey
  ) {
    throw new JobCommandResultError('JOB_COMMAND_RESULT_MISMATCH', true);
  }
}

export function validateJobCommandResult<TJob extends PersistedJobIdentity>(args: {
  data: unknown;
  orgId: string;
  title: string;
  publish: boolean;
  commandKey: string;
}) {
  if (!isRecord(args.data)) {
    throw new JobCommandResultError('JOB_PERSISTENCE_UNCONFIRMED');
  }

  const result = args.data as JobCommandResult;
  if (
    result.success !== true ||
    typeof result.draft_id !== 'string' ||
    !UUID_PATTERN.test(result.draft_id) ||
    !isRecord(result.job)
  ) {
    throw new JobCommandResultError('JOB_PERSISTENCE_UNCONFIRMED');
  }
  if (result.published !== args.publish) {
    throw new JobCommandResultError('JOB_COMMAND_RESULT_MISMATCH', true);
  }

  const job = result.job as TJob;
  assertExpectedIdentity({
    job,
    orgId: args.orgId,
    title: args.title,
    publish: args.publish,
    commandKey: args.commandKey,
  });
  if (job.id !== result.draft_id) {
    throw new JobCommandResultError('JOB_COMMAND_RESULT_MISMATCH', true);
  }

  return { job, draftId: result.draft_id };
}

export function validatePersistedJobReadBack<TJob extends PersistedJobIdentity>(args: {
  job: unknown;
  rpcJob: TJob;
  draftId: string;
  orgId: string;
  title: string;
  publish: boolean;
  commandKey: string;
}) {
  if (!isRecord(args.job)) {
    throw new JobCommandResultError('JOB_PERSISTENCE_UNCONFIRMED');
  }

  const job = args.job as TJob;
  assertExpectedIdentity({
    job,
    orgId: args.orgId,
    title: args.title,
    publish: args.publish,
    commandKey: args.commandKey,
  });
  if (job.id !== args.draftId || job.id !== args.rpcJob.id) {
    throw new JobCommandResultError('JOB_COMMAND_RESULT_MISMATCH', true);
  }
  return job;
}
